import { Link } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { BookIcon } from '../Icons/BookIcon';
import { ChartIcon } from '../Icons/ChartIcon';
import { LanguageSwitch } from '../LanguageSwitch';
import { useTranslation } from 'react-i18next';

type NavLink = {
    to: string;
    label: string; // translation key
    icon?: JSX.Element;
};
const aboutLinks: NavLink[] = [
    {
        to: '/om/data',
        label: 'data',
    },
    {
        to: '/om/om',
        label: 'om',
    },
];

const storyLinks: NavLink[] = [
    {
        to: '/om/socioekonomi',
        label: 'socioeconomy',
        icon: <BookIcon className="fill-info" />,
    },
    {
        to: '/om/lyckoparadoxen',
        label: 'happinessParadox',
        icon: <BookIcon className="fill-warning" />,
    },
    {
        to: '/om/bostader',
        label: 'housing',
        icon: <BookIcon className="fill-secondary" />,
    },
    {
        to: '/om/barn-utbildning',
        label: 'childrenEducation',
        icon: <BookIcon className="fill-error" />,
    },
    {
        to: '/om/kon-inkomst',
        label: 'genderIncome',
        icon: <BookIcon className="fill-primary" />,
    },
];

const Title = () => (
    <div className="self-center">
        <Link to="/">
            <h1 className="text-xl ">{import.meta.env.VITE_SITE_NAME}</h1>
        </Link>
    </div>
);

type MenuKey = 'stories' | 'about';

const NavItems = () => {
    const { t } = useTranslation();
    const [openMenu, setOpenMenu] = useState<MenuKey | null>(null);
    const navRef = useRef<HTMLUListElement>(null);
    const storiesTriggerRef = useRef<HTMLButtonElement>(null);
    const aboutTriggerRef = useRef<HTMLButtonElement>(null);
    const storiesMenuRef = useRef<HTMLUListElement>(null);
    const aboutMenuRef = useRef<HTMLUListElement>(null);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (navRef.current && !navRef.current.contains(e.target as Node)) {
                setOpenMenu(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Focus first item when a dropdown opens
    useEffect(() => {
        if (openMenu === 'stories') {
            const first = storiesMenuRef.current?.querySelector<HTMLElement>('[role="menuitem"]');
            first?.focus();
        } else if (openMenu === 'about') {
            const first = aboutMenuRef.current?.querySelector<HTMLElement>('[role="menuitem"]');
            first?.focus();
        }
    }, [openMenu]);

    // Keyboard navigation for open dropdown
    useEffect(() => {
        if (!openMenu) return;

        const menuRef = openMenu === 'stories' ? storiesMenuRef : aboutMenuRef;
        const triggerRef = openMenu === 'stories' ? storiesTriggerRef : aboutTriggerRef;

        const handleKeyDown = (e: KeyboardEvent) => {
            const items = Array.from(
                menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []
            );
            const focused = document.activeElement as HTMLElement;
            const idx = items.indexOf(focused);

            if (e.key === 'Escape') {
                e.preventDefault();
                setOpenMenu(null);
                triggerRef.current?.focus();
            } else if (e.key === 'ArrowDown') {
                e.preventDefault();
                items[(idx + 1) % items.length]?.focus();
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                items[(idx - 1 + items.length) % items.length]?.focus();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [openMenu]);

    const toggle = (key: MenuKey) =>
        setOpenMenu((prev) => (prev === key ? null : key));

    return (
        <ul ref={navRef} className="menu menu-horizontal px-1">
            <li>
                <Link
                    className="link"
                    activeOptions={{ exact: true }}
                    activeProps={{ className: 'link-primary font-bold' }}
                    to={'/'}
                    onClick={() => setOpenMenu(null)}
                >
                    {t('introduction')}
                </Link>
            </li>
            <li>
                <Link
                    className="link"
                    activeOptions={{ exact: true }}
                    activeProps={{ className: 'link-primary font-bold' }}
                    to={'/om/guide'}
                    onClick={() => setOpenMenu(null)}
                >
                    {t('guide')}
                </Link>
            </li>

            <li className="relative">
                <button
                    ref={storiesTriggerRef}
                    className="flex items-center gap-1"
                    onClick={() => toggle('stories')}
                    aria-expanded={openMenu === 'stories'}
                    aria-haspopup="true"
                >
                    {t('stories')}
                    <svg
                        className={`w-3 h-3 transition-transform ${openMenu === 'stories' ? 'rotate-180' : ''}`}
                        viewBox="0 0 10 6"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        aria-hidden="true"
                    >
                        <path d="M1 1l4 4 4-4" />
                    </svg>
                </button>
                {openMenu === 'stories' && (
                    <ul
                        ref={storiesMenuRef}
                        role="menu"
                        className="absolute top-full left-0 z-50 bg-base-100 rounded-box shadow-md p-2 min-w-max"
                    >
                        {storyLinks.map((link) => (
                            <li key={link.to}>
                                <Link
                                    role="menuitem"
                                    className="link"
                                    activeOptions={{ exact: true }}
                                    activeProps={{ className: 'link-primary' }}
                                    to={link.to}
                                    onClick={() => setOpenMenu(null)}
                                >
                                    {link.icon && <div aria-hidden="true">{link.icon}</div>}
                                    {t(link.label)}
                                </Link>
                            </li>
                        ))}
                    </ul>
                )}
            </li>
            <li className="relative">
                <button
                    ref={aboutTriggerRef}
                    className="flex items-center gap-1"
                    onClick={() => toggle('about')}
                    aria-expanded={openMenu === 'about'}
                    aria-haspopup="true"
                >
                    {t('about')}
                    <svg
                        className={`w-3 h-3 transition-transform ${openMenu === 'about' ? 'rotate-180' : ''}`}
                        viewBox="0 0 10 6"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        aria-hidden="true"
                    >
                        <path d="M1 1l4 4 4-4" />
                    </svg>
                </button>
                {openMenu === 'about' && (
                    <ul
                        ref={aboutMenuRef}
                        role="menu"
                        className="absolute top-full left-0 z-50 bg-base-100 rounded-box shadow-md p-2 min-w-max"
                    >
                        {aboutLinks.map((link) => (
                            <li key={link.to}>
                                <Link
                                    role="menuitem"
                                    className="link"
                                    activeOptions={{ exact: true }}
                                    activeProps={{ className: 'link-primary' }}
                                    to={link.to}
                                    onClick={() => setOpenMenu(null)}
                                >
                                    {t(link.label)}
                                </Link>
                            </li>
                        ))}
                    </ul>
                )}
            </li>
        </ul>
    );
};

const ToolButton = () => {
    const { t } = useTranslation();

    return (
        <Link to="/tool" className="btn btn-secondary no-animation text-white">
            <ChartIcon fill="white" size={30} />
            {t('till-verktyget')}
        </Link>
    );
};

export function Nav() {
    return (
        <>
            {/* Mobile */}
            <nav className="md:hidden p-3">
                <div className="flex flex-row justify-between">
                    <Title />
                    <LanguageSwitch />
                </div>
                <NavItems />
            </nav>
            {/* Desktop */}
            <nav className="hidden md:flex flex-row flex-1 justify-between p-3">
                <div className="flex flex-row gap-3">
                    <Title />
                    <NavItems />
                </div>
                <div className="flex flex-row gap-3 items-center">
                    <div>
                        <LanguageSwitch />
                    </div>
                    <ToolButton />
                </div>
            </nav>
        </>
    );
}
